import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  CalendarCheck2,
  CheckCircle2,
  MapPin,
  ShieldCheck,
  Star,
  UserRoundCheck,
  WalletCards,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactForm from '../../components/ContactForm';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import ServiceIcon from '../../components/ServiceIcon';
import { reviewsApi } from '../../api/reviewsApi';
import { servicesApi } from '../../api/servicesApi';

const TRUST_ITEMS = [
  { icon: ShieldCheck, title: 'Clear verification', text: 'Giggers complete required verification before full access.' },
  { icon: UserRoundCheck, title: 'Choose who helps', text: 'Review profiles, actual rates and availability before booking.' },
  { icon: CalendarCheck2, title: 'Manage one clear flow', text: 'Keep booking timing and progress connected to the same Gig.' },
  { icon: WalletCards, title: 'Payments stay connected', text: 'Payment activity stays tied to the booking from start to finish.' },
];

const fallbackServices = [
  { _id: 'cleaning', slug: 'cleaning', icon: 'cleaning', name: 'Cleaning', description: 'Home and apartment cleaning.' },
  { _id: 'dog-walking', slug: 'dog-walking', icon: 'dog-walking', name: 'Dog walking', description: 'Reliable walks and pet visits.' },
  { _id: 'home-cooking', slug: 'home-cooking', icon: 'cooking', name: 'Home cooking', description: 'Meal preparation and kitchen help.' },
  { _id: 'gardening', slug: 'gardening', icon: 'gardening', name: 'Gardening', description: 'Everyday yard and garden help.' },
];

const fallbackReviews = [
  {
    id: 'sample-cleaning-review',
    rating: 5,
    comment: 'Booking was simple and the Gigger was friendly, punctual and did a great job. The whole process felt easy from start to finish.',
    name: 'Emma R.',
    service: 'Cleaning',
    location: 'Melbourne',
    sample: true,
  },
  {
    id: 'sample-dog-walking-review',
    rating: 5,
    comment: 'I found someone local quickly and could see the booking details clearly. It was convenient and I would happily use Giggs again.',
    name: 'Daniel M.',
    service: 'Dog walking',
    location: 'Sydney',
    sample: true,
  },
  {
    id: 'sample-home-help-review',
    rating: 5,
    comment: 'The booking flow was straightforward and communication was easy. Having everything connected to one Gig made it much simpler to manage.',
    name: 'Priya K.',
    service: 'Everyday help',
    location: 'Brisbane',
    sample: true,
  },
];

const Home = () => {
  const [services, setServices] = useState(fallbackServices);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    servicesApi.list().then((data) => {
      if (active && Array.isArray(data) && data.length) setServices(data);
    }).catch(() => {});
    reviewsApi.publicList(6).then((response) => {
      if (active) setReviews(Array.isArray(response?.data) ? response.data : []);
    }).catch(() => {
      if (active) setReviews([]);
    }).finally(() => {
      if (active) setReviewsLoaded(true);
    });
    return () => { active = false; };
  }, []);

  const displayReviews = reviews.length ? reviews : fallbackReviews;
  const showingSampleReviews = reviewsLoaded && reviews.length === 0;

  return (
    <>
      <Header />
      <title>Giggs | Find Trusted Local Service Providers in Australia</title>
      <main className="w-full overflow-x-hidden bg-white font-dm text-slate-800">
        <section className="relative overflow-hidden bg-gradient-to-br from-[#EAF8F0] via-white to-[#F6FBF8] px-4 py-11 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
          <div className="absolute -left-28 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-emerald-200/30 blur-3xl" />
          <div className="relative mx-auto grid w-full max-w-[1500px] items-center gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-12">
            <div className="min-w-0">
              <span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">Local help. Flexible work.</span>
              <h1 className="mb-0 mt-3 max-w-3xl font-nunito text-[clamp(2.35rem,5.5vw,4.6rem)] font-bold leading-[1.04] tracking-[-0.025em] text-slate-950">Get everyday help done with the right local Gigger</h1>
              <p className="mb-0 mt-5 max-w-2xl text-base font-normal leading-7 text-slate-600 lg:text-lg">Choose what you need, compare available Giggers and manage the Gig through one clear platform.</p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link to="/book" className="inline-flex min-h-16 items-center justify-center gap-2 rounded-2xl bg-green-700 px-8 py-4 text-base font-extrabold text-white no-underline shadow-[0_16px_34px_rgba(21,128,61,0.28)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-800 hover:shadow-[0_20px_40px_rgba(21,128,61,0.32)] sm:min-w-[190px] sm:px-9 sm:text-lg">Book local help <ArrowRight size={19} /></Link>
                <Link to="/provider-register" className="inline-flex min-h-16 items-center justify-center rounded-2xl border-2 border-green-600/40 bg-white px-8 py-4 text-base font-extrabold text-green-800 no-underline shadow-[0_12px_28px_rgba(15,23,42,0.10)] transition-all duration-200 hover:-translate-y-0.5 hover:border-green-600/60 hover:bg-green-50 hover:shadow-[0_16px_34px_rgba(15,23,42,0.12)] sm:min-w-[190px] sm:px-9 sm:text-lg">Become a Gigger</Link>
              </div>
              <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-slate-500">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-green-700" /> Guided booking</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-green-700" /> Local availability</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-green-700" /> Clear next steps</span>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-3xl lg:mx-0 lg:ml-auto">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="row-span-2 overflow-hidden rounded-[2rem] shadow-[0_22px_55px_rgba(15,23,42,0.16)]">
                  <video
                    src="/gif/cleaning_floor.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label="Local cleaning service"
                    className="h-full min-h-[360px] w-full object-cover"
                  />
                </div>

                <div className="overflow-hidden rounded-[1.6rem] shadow-[0_18px_45px_rgba(15,23,42,0.12)]">
                  <video
                    src="/gif/washing_dishes.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label="Errands and grocery runs through Giggs"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>

                <div className="overflow-hidden rounded-[1.6rem] shadow-[0_18px_45px_rgba(15,23,42,0.12)]">
                  <video
                    src="/gif/moving_help.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label="Moving help through Giggs"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>
              </div>
              <div className="absolute -bottom-4 left-3 right-3 rounded-2xl bg-white/95 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.14)] backdrop-blur sm:-bottom-5 sm:left-8 sm:right-auto sm:w-80">
                <div className="flex items-center gap-3"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-100 text-green-700"><MapPin size={20} /></span><div><p className="m-0 text-sm font-semibold text-slate-900">Local help, one clear flow</p><p className="mb-0 mt-1 text-xs font-normal leading-5 text-slate-500">Choose a service, date and Gigger, then track your Gig.</p></div></div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-100 bg-white px-4 py-8 sm:px-6 lg:px-8 2xl:px-10">
          <div className="mx-auto grid w-full max-w-[1500px] gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {TRUST_ITEMS.map(({ icon: Icon, title, text }) => (
              <article key={title} className="flex min-w-0 gap-4 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/[0.04] sm:p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-700"><Icon size={21} /></span>
                <div><h2 className="m-0 font-nunito text-sm font-bold text-slate-950">{title}</h2><p className="mb-0 mt-1 text-xs font-normal leading-5 text-slate-500">{text}</p></div>
              </article>
            ))}
          </div>
        </section>

        <section className="px-4 py-11 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
          <div className="mx-auto w-full max-w-[1500px]">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div><span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">Services</span><h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,3.5vw,2.8rem)] font-bold tracking-[-0.015em] text-slate-950">Choose the help you need</h2><p className="mb-0 mt-3 max-w-2xl text-sm font-normal leading-6 text-slate-600 lg:text-base">Start with a service and Giggs will guide you through the rest of the booking.</p></div>
              <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-green-700 no-underline transition-all hover:gap-3">View all services <ArrowRight size={17} /></Link>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {services.slice(0, 8).map((service) => {
                const comingSoon = service.status === 'Coming Soon' || service.comingSoon;
                const card = <><span className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 text-green-700"><ServiceIcon serviceId={service.icon || service.slug || service._id} size={23} /></span><h3 className="mb-0 mt-5 font-nunito text-lg font-bold text-slate-950">{service.name}</h3><p className="mb-0 mt-2 text-sm font-normal leading-6 text-slate-500">{service.description || 'Trusted local help through one clear booking flow.'}</p><span className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${comingSoon ? 'text-slate-400' : 'text-green-700'}`}>{comingSoon ? 'Coming soon' : 'Book service'} {!comingSoon && <ArrowRight size={16} />}</span></>;
                const cls = 'rounded-2xl bg-white p-5 no-underline shadow-[0_8px_28px_rgba(15,23,42,0.055)] ring-1 ring-black/[0.045] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_38px_rgba(15,23,42,0.09)]';
                return comingSoon ? <article key={service._id || service.slug} className={cls}>{card}</article> : <Link key={service._id || service.slug} to={`/book?service=${service.slug || service._id}`} className={cls}>{card}</Link>;
              })}
            </div>
          </div>
        </section>

        <section className="bg-[#F5FAF7] px-4 py-11 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
          <div className="mx-auto w-full max-w-[1500px]">
            <div className="mx-auto max-w-4xl text-center"><span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">One platform, two paths</span><h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,3.5vw,2.8rem)] font-bold tracking-[-0.015em] text-slate-950">Giggs should make sense from the first click</h2><p className="mb-0 mt-3 text-sm font-normal leading-6 text-slate-600 lg:text-base">Choose the path that matches what you are here to do.</p></div>

            <div className="mt-8 grid gap-6 xl:grid-cols-2">
              <article className="group overflow-hidden rounded-[2rem] bg-white shadow-[0_12px_36px_rgba(15,23,42,0.07)] ring-1 ring-black/[0.04]">
                <div className="grid md:grid-cols-[0.9fr_1.1fr]">
                  <div className="overflow-hidden"><video src="/gif/hand_made_jobs.mp4" autoPlay muted loop playsInline preload="metadata" aria-label="Client receiving everyday help" className="aspect-[16/10] h-full w-full object-cover md:aspect-auto" /></div>
                  <div className="p-6 sm:p-8"><span className="rounded-full bg-green-100 px-3 py-1.5 text-xs font-medium text-green-800">For clients</span><h3 className="mb-0 mt-4 font-nunito text-2xl font-bold text-slate-950">Get a Gig done</h3><p className="mb-0 mt-3 text-sm font-normal leading-6 text-slate-600">Choose your service, location and date, compare available Giggers and send your booking request.</p><ul className="mt-5 list-none space-y-3 p-0 text-sm font-normal text-slate-700"><li className="flex gap-2"><CheckCircle2 size={18} className="text-green-700" /> Browse local services</li><li className="flex gap-2"><CheckCircle2 size={18} className="text-green-700" /> Compare availability and actual rates</li><li className="flex gap-2"><CheckCircle2 size={18} className="text-green-700" /> Track the Gig from one place</li></ul><Link to="/book" className="mt-6 inline-flex rounded-xl bg-green-700 px-4 py-3 text-sm font-semibold text-white no-underline hover:bg-green-800">Book a service</Link></div>
                </div>
              </article>

              <article className="group overflow-hidden rounded-[2rem] bg-[#0B1D14] text-white shadow-[0_12px_36px_rgba(15,23,42,0.16)]">
                <div className="grid md:grid-cols-[0.9fr_1.1fr]">
                  <div className="overflow-hidden"><video src="/gif/cleaning.mp4" autoPlay muted loop playsInline preload="metadata" aria-label="Gigger choosing flexible work" className="aspect-[16/10] h-full w-full object-cover md:aspect-auto" /></div>
                  <div className="p-6 sm:p-8"><span className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-emerald-200">For Giggers</span><h3 className="mb-0 mt-4 font-nunito text-2xl font-bold text-white">Find your next Gig</h3><p className="mb-0 mt-3 text-sm font-normal leading-6 text-white/70">Build your profile, choose the services you offer, set one actual rate and manage availability around your schedule.</p><ul className="mt-5 list-none space-y-3 p-0 text-sm font-normal text-white/80"><li className="flex gap-2"><CheckCircle2 size={18} className="text-emerald-300" /> Choose the work you want</li><li className="flex gap-2"><CheckCircle2 size={18} className="text-emerald-300" /> Set your availability and service area</li><li className="flex gap-2"><CheckCircle2 size={18} className="text-emerald-300" /> Complete verification and payouts</li></ul><Link to="/provider-register" className="mt-6 inline-flex rounded-xl bg-white px-4 py-3 text-sm font-semibold text-slate-900 no-underline hover:bg-green-50">Become a Gigger</Link></div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="px-4 py-11 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
          <div className="mx-auto grid w-full max-w-[1500px] items-center gap-9 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12">
            <div><span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">How booking works</span><h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,3.4vw,2.7rem)] font-bold text-slate-950">From “I need help” to a confirmed Gig</h2><p className="mb-0 mt-4 text-base font-normal leading-7 text-slate-600">The booking flow explains what happens next at every stage, with no account required just to begin.</p><Link to="/how-it-works" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3.5 text-sm font-semibold text-white no-underline hover:bg-green-800">See the full process <ArrowRight size={17} /></Link></div>
            <div className="overflow-hidden rounded-[2rem] shadow-[0_18px_48px_rgba(15,23,42,0.12)]"><video src="/gif/gardening.mp4" autoPlay muted loop playsInline preload="metadata" aria-label="Verified local Giggers on Giggs" className="aspect-[16/9] w-full object-cover" /></div>
          </div>
        </section>

        <section className="bg-green-950 px-4 py-11 text-white sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
          <div className="mx-auto w-full max-w-[1500px]">
            <div className="grid gap-6 lg:grid-cols-[0.65fr_1.35fr] lg:gap-10">
              <div>
                <span className="text-xs font-medium uppercase tracking-[0.14em] text-green-300">{showingSampleReviews ? 'Sample testimonials' : 'Real client reviews'}</span>
                <h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,3.4vw,2.7rem)] font-bold text-white">What Giggs clients say</h2>
                <p className="mb-0 mt-4 max-w-lg text-sm font-normal leading-6 text-green-50/70">{showingSampleReviews ? 'Temporary sample feedback is shown while completed Gig reviews start coming in.' : 'Reviews submitted through completed Giggs bookings are shown here.'}</p>
              </div>
              <div>
                {!reviewsLoaded ? <div className="rounded-2xl bg-white/10 px-5 py-10 text-center text-sm text-white/60">Loading reviews…</div> : (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {displayReviews.map((review) => (
                      <article key={review.id} className="rounded-2xl bg-white/10 p-5 ring-1 ring-inset ring-white/10 sm:p-6">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex gap-1 text-amber-300" aria-label={`${review.rating} out of 5 stars`}>{Array.from({ length: Math.max(0, Math.min(5, Number(review.rating) || 0)) }).map((_, index) => <Star key={index} size={15} fill="currentColor" />)}</div>
                          {review.sample && <span className="rounded-full bg-white/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/55">Sample</span>}
                        </div>
                        <p className="mb-0 mt-4 text-sm font-normal leading-6 text-white">“{review.comment}”</p>
                        <div className="mt-5 border-t border-white/10 pt-4"><p className="m-0 text-sm font-semibold text-white">{review.name}</p><p className="mb-0 mt-1 text-xs font-normal text-green-100/65">{[review.service, review.location].filter(Boolean).join(' · ')}</p></div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="bg-slate-50 px-4 py-11 sm:px-6 sm:py-14 lg:px-8 lg:py-16 2xl:px-10">
          <div className="mx-auto grid w-full max-w-[1500px] gap-8 xl:grid-cols-[0.8fr_1.2fr] xl:items-center xl:gap-10">
            <div><span className="text-xs font-medium uppercase tracking-[0.14em] text-green-700">Contact us</span><h2 className="mb-0 mt-2 font-nunito text-[clamp(1.8rem,3.4vw,2.7rem)] font-bold text-slate-950">Need help before you get started?</h2><p className="mb-0 mt-4 text-base font-normal leading-7 text-slate-600">Send the Giggs team a message about booking, becoming a Gigger, your account or an existing Gig.</p></div>
            <ContactForm />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Home;
